import { SagaIterator } from "redux-saga";
import { all, call, put, select, takeLatest } from "redux-saga/effects";

import { config } from "config/config";
import { Cookie } from "constants/cookies";
import { ToastContent, getToastContent } from "constants/toasts";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { getCookie, removeCookie } from "helpers/cookies";
import { buildPlanCode } from "helpers/subscription";
import { COUPON_COOKIE_OPTIONS } from "helpers/subscription-plans";
import { getLicenseSourceParams } from "helpers/url";
import { ApiManager } from "services/api";
import { LicenseSerializer } from "services/api/license";
import { CRUDAction, RequestAction } from "store/entities/actions";
import { LICENSE, LicenseActions } from "store/entities/license";
import { BillingCycle, Plan } from "store/entities/subscriptions";
import { ToastVariant, ToastsActions } from "store/features/toasts";
import { IActionWithPayload } from "store/helper";
import { IUpdateDefaultMailboxPayload } from "store/views/settings/interfaces";

import { selectLoggedInAgentLogin } from "../agents";
import { IntegrationsActions, selectLiveChatIntegration } from "../integrations";

import {
  IFetchLicensePayload,
  IResendVerificationSuccessPayload,
  ISendForwardingInstructionsPayload,
  ISetCompanyNamePayload,
  ISetLicenseFlagPayload,
  ISetPartnerIdPayload,
  ISetPoweredByFooterPayload,
  ISetSourceFirstClick,
  ISetUkraineFooterPayload,
  IUpdateDefaultTeamPayload,
  IUpdateDefaultTemplatePayload,
  IVerifyLicensePayload,
} from "./interfaces";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* fetch(action: IActionWithPayload<string, IFetchLicensePayload>) {
  const { id } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.licenseApi.fetch, String(id));

  if (response.result) {
    yield put(LicenseActions.fetchSuccess({ id, value: LicenseSerializer.deserialize(response.result) }));
  } else {
    yield put(LicenseActions.fetchFailure({ id, error: null }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* fetchCollection() {
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.licenseApi.fetchCollection);

  if (response.result) {
    yield put(
      LicenseActions.fetchCollectionSuccess({ values: LicenseSerializer.deserializeCollection(response.result) })
    );
  } else {
    yield put(LicenseActions.fetchCollectionFailure({ error: null }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* create() {
  const planCode = buildPlanCode(Plan.Team, BillingCycle.Monthly);
  const couponCode = getCookie(Cookie.CouponCode);
  const sourceParams = getLicenseSourceParams();
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.licenseApi.create, planCode, couponCode, sourceParams);

  if (response.result) {
    const license = LicenseSerializer.deserialize(response.result);
    yield put(LicenseActions.createSuccess({ id: String(license.id), value: license }));
  } else if (
    couponCode &&
    response.responseStatus === 422 &&
    response.error &&
    response.error.error &&
    response.error.error.details &&
    response.error.error.details.source === "accounts"
  ) {
    // remove incorrect couponCode and retry creating license
    removeCookie(Cookie.CouponCode, COUPON_COOKIE_OPTIONS);
    yield put(LicenseActions.create({ value: {} }));
  } else {
    yield put(LicenseActions.createFailure({ error: null }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* setCompanyName(action: IActionWithPayload<string, ISetCompanyNamePayload>) {
  const { companyName, requestSource } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.licenseApi.updateCompanyName, companyName);

  if (response.result) {
    const license = LicenseSerializer.deserialize(response.result);
    yield put(LicenseActions.setCompanyNameSuccess({ id: String(license.id), value: license, requestSource }));
  } else {
    // @ts-expect-error TS(2322)
    yield put(LicenseActions.setCompanyNameFailure({ id: null, error: null, requestSource }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* setPartnerId(action: IActionWithPayload<string, ISetPartnerIdPayload>) {
  const { partnerId } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.licenseApi.updatePartnerId, String(partnerId));

  const cookieOptions = {
    path: "/",
    domain: config.cookieDomain,
  };

  if (response.result) {
    removeCookie(Cookie.PartnerId, cookieOptions);
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* setSourceFirstClick(action: IActionWithPayload<string, ISetSourceFirstClick>) {
  const SourceFirstClick: ISetSourceFirstClick = action.payload;

  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.licenseApi.updateSourceFirstClick, SourceFirstClick);

  const cookieOptions = {
    path: "/",
    domain: config.cookieDomain,
  };

  if (response.result) {
    Object.keys(SourceFirstClick).forEach(source => {
      removeCookie(source, cookieOptions);
    });
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* setUkraineSupportFooter(action: IActionWithPayload<string, ISetUkraineFooterPayload>) {
  const { supportUkraineFooter, requestSource } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.licenseApi.updateUkraineSupportFooter, supportUkraineFooter);

  if (response.result) {
    const license = LicenseSerializer.deserialize(response.result);
    yield put(LicenseActions.setUkraineSupportFooterSuccess({ id: String(license.id), value: license, requestSource }));
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.UKRAINE_SUPPORT_FOOTER_SUCCESS),
        variant: ToastVariant.Success,
      })
    );
  } else {
    // @ts-expect-error TS(2322)
    yield put(LicenseActions.setUkraineSupportFooterFailure({ id: null, error: null, requestSource }));
    yield put(
      ToastsActions.createToast({
        // @ts-expect-error TS(2551): Property 'UKRAINE_SUPPORT_FOOTER_FAILURE' does not... Remove this comment to see the full error message
        content: getToastContent(ToastContent.UKRAINE_SUPPORT_FOOTER_FAILURE),
        variant: ToastVariant.Error,
      })
    );
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* setPoweredByFooter(action: IActionWithPayload<string, ISetPoweredByFooterPayload>) {
  const { poweredByFooter, requestSource } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.licenseApi.updatePoweredByFooter, poweredByFooter);

  if (response.result) {
    const license = LicenseSerializer.deserialize(response.result);
    yield put(LicenseActions.setPoweredByFooterSuccess({ id: String(license.id), value: license, requestSource }));
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.POWERED_BY_FOOTER_SUCCESS),
        variant: ToastVariant.Success,
      })
    );
  } else {
    // @ts-expect-error TS(2322)
    yield put(LicenseActions.setPoweredByFooterFailure({ id: null, error: null, requestSource }));
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.POWERED_BY_FOOTER_SUCCESS),
        variant: ToastVariant.Error,
      })
    );
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateDefaultTeam(action: IActionWithPayload<string, IUpdateDefaultTeamPayload>) {
  const { defaultTeamId } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.licenseApi.updateDefaultTeam, defaultTeamId);

  if (response.result) {
    const license = LicenseSerializer.deserialize(response.result);
    yield put(LicenseActions.updateDefaultTeamSuccess({ id: String(license.id), value: license }));
  } else {
    // @ts-expect-error TS(2322)
    yield put(LicenseActions.updateDefaultTeamFailure({ id: null, error: null }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateDefaultTemplate(action: IActionWithPayload<string, IUpdateDefaultTemplatePayload>) {
  const { defaultTemplateId } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.licenseApi.updateDefaultTemplate, defaultTemplateId);

  if (response.result) {
    const license = LicenseSerializer.deserialize(response.result);
    yield put(LicenseActions.updateDefaultTemplateSuccess({ id: String(license.id), value: license }));
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.UPDATE_DEFAULT_TEMPLATE_SUCCESS),
        variant: ToastVariant.Success,
      })
    );
    yield call(trackEngagement, "Email templates: Template made default", EventPlace.Settings);
  } else {
    // @ts-expect-error TS(2322)
    yield put(LicenseActions.updateDefaultTemplateFailure({ id: null, error: null }));
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.UPDATE_DEFAULT_TEMPLATE_FAILURE),
        variant: ToastVariant.Error,
      })
    );
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* sendForwardingInstructions(action: IActionWithPayload<string, ISendForwardingInstructionsPayload>) {
  const { recipientEmail, requestSource } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.licenseApi.sendForwardingInstructions, recipientEmail);
  if (response.result) {
    yield put(LicenseActions.sendForwardingInstructionsSuccess({ requestSource }));
  } else {
    yield put(
      LicenseActions.sendForwardingInstructionsFailure({
        requestSource,
        error: "Could not send instructions.",
        errorObject: response?.error?.error,
      })
    );
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateDefaultMailbox(action: IActionWithPayload<string, IUpdateDefaultMailboxPayload>) {
  const { requestSource, ...payload } = action.payload;

  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.licenseApi.updateDefaultMailbox, payload);

  if (response.result) {
    // @ts-expect-error TS(2322)
    yield put(LicenseActions.updateDefaultMailboxSuccess({ requestSource }));
  } else {
    // @ts-expect-error TS(2322)
    yield put(LicenseActions.updateDefaultMailboxFailure({ requestSource, error: "Failed to update default inbox" }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* verify(action: IActionWithPayload<string, IVerifyLicensePayload>) {
  const { salt, hash } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.licenseApi.verify, salt, hash);

  if (response.result) {
    yield put(LicenseActions.verifySuccess());
  } else {
    yield put(LicenseActions.verifyFailure({ error: response.error }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* resendVerification() {
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.agentApi.resendVerification);

  if (response.result) {
    // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
    const email = yield select(selectLoggedInAgentLogin);
    yield put(LicenseActions.resendVerificationSuccess({ email }));
  } else {
    yield put(LicenseActions.resendVerificationFailure({ error: response.error }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* resendVerificationSuccess(action: IActionWithPayload<string, IResendVerificationSuccessPayload>) {
  const { email } = action.payload;

  yield put(
    ToastsActions.createToast({
      variant: ToastVariant.Success,
      content: getToastContent(ToastContent.RESEND_VERIFICATION_SUCCESS, { email }),
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* fetchLivechatProperties() {
  try {
    // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
    const response = yield call(ApiManager.integrationsApi.fetchLivechatLicenseProperties);

    if (response.result) {
      yield put(LicenseActions.fetchLivechatPropertiesSuccess(response.result));
    } else {
      yield put(LicenseActions.fetchLivechatPropertiesFailure());
    }
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    yield put(LicenseActions.fetchLivechatPropertiesFailure());
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* fetchLivechatPropertiesIfNeeded() {
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const livechatIntegration = yield select(selectLiveChatIntegration);
  const hasLivechatIntegration = livechatIntegration !== undefined;

  if (hasLivechatIntegration) {
    yield put(LicenseActions.fetchLivechatProperties());
    yield put(IntegrationsActions.fetchLivechatGroups());
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* clearGoogleForwarding() {
  yield call(ApiManager.licenseApi.clearGoogleForwarding);
}

function* setLicenseFlag(action: IActionWithPayload<string, ISetLicenseFlagPayload>): SagaIterator {
  const { flag, hash, value } = action.payload;
  yield call(ApiManager.licenseApi.setLicenseFlag, flag, hash, value);
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* licenseSagas() {
  yield all([
    takeLatest(LICENSE.VERIFY[RequestAction.REQUEST], verify),
    takeLatest(LICENSE[CRUDAction.FETCH_SINGLE][RequestAction.REQUEST], fetch),
    takeLatest(LICENSE[CRUDAction.FETCH_COLLECTION][RequestAction.REQUEST], fetchCollection),
    takeLatest(LICENSE[CRUDAction.CREATE][RequestAction.REQUEST], create),
    takeLatest(LICENSE.SET_COMPANY_NAME[RequestAction.REQUEST], setCompanyName),
    takeLatest(LICENSE.SET_UKRAINE_SUPPORT_FOOTER[RequestAction.REQUEST], setUkraineSupportFooter),
    takeLatest(LICENSE.SET_POWERED_BY_FOOTER[RequestAction.REQUEST], setPoweredByFooter),
    takeLatest(LICENSE.SET_PARTNER_ID[RequestAction.REQUEST], setPartnerId),
    takeLatest(LICENSE.SET_SOURCE_FIRST_CLICK[RequestAction.REQUEST], setSourceFirstClick),
    takeLatest(LICENSE.UPDATE_DEFAULT_TEAM[RequestAction.REQUEST], updateDefaultTeam),
    takeLatest(LICENSE.UPDATE_DEFAULT_TEMPLATE[RequestAction.REQUEST], updateDefaultTemplate),
    takeLatest(LICENSE.UPDATE_DEFAULT_MAILBOX[RequestAction.REQUEST], updateDefaultMailbox),
    takeLatest(LICENSE.SEND_FORWARDING_INSTRUCTIONS[RequestAction.REQUEST], sendForwardingInstructions),
    takeLatest(LICENSE.RESEND_VERIFICATION[RequestAction.REQUEST], resendVerification),
    takeLatest(LICENSE.RESEND_VERIFICATION[RequestAction.SUCCESS], resendVerificationSuccess),
    takeLatest(LICENSE.FETCH_LIVECHAT_PROPERTIES[RequestAction.REQUEST], fetchLivechatProperties),
    takeLatest(LICENSE[CRUDAction.FETCH_SINGLE][RequestAction.SUCCESS], fetchLivechatPropertiesIfNeeded),
    takeLatest(LICENSE.CLEAR_GOOGLE_FORWARDING_LINK, clearGoogleForwarding),
    takeLatest(LICENSE.SET_LICENSE_FLAG, setLicenseFlag),
  ]);
}
